/*
  One analytics entry point for the whole site.

  Before this, `trackEvent` was redefined in six files with two different guards,
  and event names were invented per button — `marketing_hero_start_building_click`,
  `aeo_deploy_click`, `aeo_contact_click` — which cannot be compared to each
  other in any GA4 report.

  Rules enforced here:
    1. One event name per KIND of interaction; the specifics go in parameters.
    2. Never send PII. Google's terms prohibit email/phone/name in event params
       and breaching them can terminate the property. Send `has_phone: true`,
       never the number.
*/

/** Canonical event names. Add here, never inline, so names cannot drift. */
export const EVENTS = {
  PAGE_VIEW: 'page_view',
  CTA_CLICK: 'cta_click',
  FORM_START: 'form_start',
  GENERATE_LEAD: 'generate_lead',
  LEAD_SUBMIT_FAILED: 'lead_submit_failed',
  FAQ_EXPANDED: 'faq_expanded',
  OUTBOUND_CLICK: 'outbound_click',
  CALCULATOR_USED: 'calculator_used',
  SCROLL_DEPTH: 'scroll_depth',
};

// Parameter keys that must never carry personal data, whatever a caller passes.
const BANNED_PARAMS = new Set([
  'email', 'work_email', 'phone', 'name', 'full_name', 'company',
  'message', 'job_title', 'first_name', 'last_name',
]);

function scrub(params) {
  const clean = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (BANNED_PARAMS.has(k.toLowerCase())) {
      if (process.env.NODE_ENV !== 'production') {
        // Loud in dev, silent in prod — the value is dropped either way.
        console.warn(`[analytics] dropped "${k}": PII must not be sent to GA4.`);
      }
      return;
    }
    if (v === undefined || v === null || v === '') return;
    clean[k] = v;
  });
  return clean;
}

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, scrub(params));
  } catch (_) {
    /* analytics must never break the page */
  }
}

/**
 * Explicit page view for client-side route changes.
 * gtag('config') only fires on first load; React Router navigation does not
 * re-trigger it, so without this every internal route can go unrecorded.
 */
export function trackPageView(pathname) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', EVENTS.PAGE_VIEW, {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch (_) {
    /* no-op */
  }
}

/**
 * Fires once per session per label. For things like form_start or first
 * calculator interaction, where repeat firing is noise rather than signal.
 */
const fired = new Set();
export function trackOnce(eventName, params = {}, key = '') {
  const id = `${eventName}:${key || params.page || params.location || ''}`;
  if (fired.has(id)) return;
  fired.add(id);
  trackEvent(eventName, params);
}

export default trackEvent;
